/**
 * Zustand persist helpers for `useAppStore` (`store/store.ts`). Extracted for unit tests without booting the full store.
 */

import { ColorSchemePreferenceEnum } from '../../enums/ColorSchemePreferenceEnum';
import type { FolderContentStatsCacheEntry } from '../../interfaces/FolderContentStatsSlice';
import { pruneUndisplayableNotifications } from '../../lib/notifications/displayableNotificationEventTypes';
import { FOLDER_STATS_CACHE_MAX_AGE_MS } from '../folderContentStatsSlice';

type AppEventsPersistShape = {
  activateTotp?: { neverShowAgain?: boolean; remindAt?: number | null };
  ssoLogin?: { isSsoLogin?: boolean };
  tooManyRequests?: { lockedUntil?: number | null };
  fileShare?: { isNewFilesShareRecipientsReadOnly?: boolean; newRecipientsReadOnly?: boolean };
  tagsList?: { isExpanded?: boolean };
};

export type AppStatePersistMergeTarget = {
  appEvents: {
    activateTotp: { neverShowAgain: boolean; remindAt: number | null; [k: string]: unknown };
    ssoLogin: { isSsoLogin: boolean; [k: string]: unknown };
    tooManyRequests: { lockedUntil: number | null; [k: string]: unknown };
    fileShare: { isNewFilesShareRecipientsReadOnly: boolean; [k: string]: unknown };
    tagsList: { isExpanded: boolean; [k: string]: unknown };
    [k: string]: unknown;
  };
  [k: string]: unknown;
};

export function mergeAppPersistedState(
  persistedState: unknown,
  currentState: unknown,
): AppStatePersistMergeTarget {
  const current = currentState as AppStatePersistMergeTarget;
  const persisted = (persistedState as Partial<AppStatePersistMergeTarget>) ?? {};
  const persistedAppEvents = persisted.appEvents as
    (AppStatePersistMergeTarget['appEvents'] & AppEventsPersistShape) | undefined;

  return {
    ...current,
    ...persisted,
    // Legacy seeds used `{}`; slice APIs expect an array (`.filter` / `.find`).
    filePasswordSessions: Array.isArray(persisted.filePasswordSessions)
      ? persisted.filePasswordSessions
      : current.filePasswordSessions,
    appEvents: persistedAppEvents
      ? {
          ...current.appEvents,
          ...persistedAppEvents,
          activateTotp: {
            ...current.appEvents.activateTotp,
            ...persistedAppEvents.activateTotp,
          },
          ssoLogin: {
            ...current.appEvents.ssoLogin,
            ...persistedAppEvents.ssoLogin,
          },
          tooManyRequests: {
            ...current.appEvents.tooManyRequests,
            ...persistedAppEvents.tooManyRequests,
          },
          fileShare: {
            ...current.appEvents.fileShare,
            ...persistedAppEvents.fileShare,
          },
          tagsList: {
            ...current.appEvents.tagsList,
            ...persistedAppEvents.tagsList,
          },
        }
      : current.appEvents,
  };
}

export function migrateAppPersistedState(persisted: unknown): unknown {
  if (!persisted) return persisted;
  const p = persisted as Record<string, unknown>;
  const fileThumbnailSkipUntil =
    p.fileThumbnailSkipUntil ?? (p as { thumbnailSkipUntil?: unknown }).thumbnailSkipUntil ?? {};
  const appEvents = (p.appEvents as Record<string, unknown> | undefined) ?? {};
  const activateTotp = (appEvents.activateTotp as Record<string, unknown> | undefined) ?? {};
  const ssoLogin = (appEvents.ssoLogin as Record<string, unknown> | undefined) ?? {};
  const tooManyRequests = (appEvents.tooManyRequests as Record<string, unknown> | undefined) ?? {};
  const fileShare = (appEvents.fileShare as Record<string, unknown> | undefined) ?? {};
  const tagsList = (appEvents.tagsList as Record<string, unknown> | undefined) ?? {};
  const { fileShareNewRecipientsReadOnly: legacyFileShareReadOnly, ...rest } = p;
  const colorSchemePreference =
    p.colorSchemePreference === ColorSchemePreferenceEnum.Light ||
    p.colorSchemePreference === ColorSchemePreferenceEnum.Dark ||
    p.colorSchemePreference === ColorSchemePreferenceEnum.System
      ? p.colorSchemePreference
      : ColorSchemePreferenceEnum.Light;
  const highContrastEnabled =
    typeof p.highContrastEnabled === 'boolean' ? p.highContrastEnabled : false;
  const reduceMotionEnabled =
    typeof p.reduceMotionEnabled === 'boolean' ? p.reduceMotionEnabled : false;
  const notifications = pruneUndisplayableNotifications(p.notifications);
  return {
    ...rest,
    colorSchemePreference,
    highContrastEnabled,
    reduceMotionEnabled,
    filePasswordSessions: Array.isArray(p.filePasswordSessions) ? p.filePasswordSessions : [],
    notifications,
    unreadCount: notifications.filter((notification) => !notification.isRead).length,
    appEvents: {
      ...appEvents,
      activateTotp: {
        remindAt:
          typeof activateTotp.remindAt === 'number' ? (activateTotp.remindAt as number) : null,
        neverShowAgain: Boolean(activateTotp.neverShowAgain),
      },
      ssoLogin: {
        isSsoLogin: Boolean(ssoLogin.isSsoLogin),
      },
      tooManyRequests: {
        lockedUntil:
          typeof tooManyRequests.lockedUntil === 'number'
            ? (tooManyRequests.lockedUntil as number)
            : null,
      },
      fileShare: {
        isNewFilesShareRecipientsReadOnly:
          typeof fileShare.isNewFilesShareRecipientsReadOnly === 'boolean'
            ? fileShare.isNewFilesShareRecipientsReadOnly
            : typeof fileShare.newRecipientsReadOnly === 'boolean'
              ? fileShare.newRecipientsReadOnly
              : typeof legacyFileShareReadOnly === 'boolean'
                ? legacyFileShareReadOnly
                : true,
      },
      tagsList: {
        isExpanded: typeof tagsList.isExpanded === 'boolean' ? tagsList.isExpanded : false,
      },
    },
    fileThumbnailSkipUntil,
    folderContentStatsCache:
      p.folderContentStatsCache && typeof p.folderContentStatsCache === 'object'
        ? p.folderContentStatsCache
        : {},
  };
}

export type PartialPersistAppStateInput = {
  locale: unknown;
  colorSchemePreference: unknown;
  highContrastEnabled: unknown;
  reduceMotionEnabled: unknown;
  customTokens: unknown;
  directOAuthProviders: unknown;
  authenticatedUserEmail: unknown;
  notifications: unknown;
  unreadCount: unknown;
  emailUnreadCount: unknown;
  onBehalfOfExpiresAt: unknown;
  onBehalfOfUser: unknown;
  filePasswordSessions: unknown;
  folderContentStatsCache?: Record<number, FolderContentStatsCacheEntry>;
  filesThumbnails?: Record<string, { token: string; expiresAt: number; fileThumbnailUrl?: string }>;
  fileThumbnailSkipUntil?: Record<string, number>;
  /** Persisted preference only — open/session fields stay out of partialize. */
  viewerDrawerSizeVh?: number;
  appEvents: {
    activateTotp: { neverShowAgain: boolean; remindAt: number | null };
    ssoLogin: { isSsoLogin: boolean };
    tooManyRequests: { lockedUntil: number | null };
    fileShare: { isNewFilesShareRecipientsReadOnly: boolean };
    tagsList: { isExpanded: boolean };
  };
};

export function partialAppPersistedState(s: PartialPersistAppStateInput): Record<string, unknown> {
  const now = Date.now();
  const rawFolderStatsCache = s.folderContentStatsCache ?? {};
  const folderContentStatsCache = Object.fromEntries(
    Object.entries(rawFolderStatsCache).filter(([, entry]) => {
      if (!entry || typeof entry.fetchedAt !== 'number') {
        return false;
      }
      return now - entry.fetchedAt <= FOLDER_STATS_CACHE_MAX_AGE_MS;
    }),
  );

  return {
    locale: s.locale,
    colorSchemePreference: s.colorSchemePreference,
    highContrastEnabled: s.highContrastEnabled,
    reduceMotionEnabled: s.reduceMotionEnabled,
    customTokens: s.customTokens,
    directOAuthProviders: s.directOAuthProviders,
    authenticatedUserEmail: s.authenticatedUserEmail,
    notifications: s.notifications,
    unreadCount: s.unreadCount,
    emailUnreadCount: s.emailUnreadCount,
    onBehalfOfExpiresAt: s.onBehalfOfExpiresAt,
    onBehalfOfUser: s.onBehalfOfUser,
    filePasswordSessions: s.filePasswordSessions,
    folderContentStatsCache,
    filesThumbnails: Object.fromEntries(
      Object.entries(s.filesThumbnails || {}).map(([fileId, entry]) => [
        fileId,
        {
          token: entry.token,
          expiresAt: entry.expiresAt,
        },
      ]),
    ),
    fileThumbnailSkipUntil:
      s.fileThumbnailSkipUntil && Object.keys(s.fileThumbnailSkipUntil).length > 0
        ? Object.fromEntries(
            Object.entries(s.fileThumbnailSkipUntil).filter(
              ([_, skipUntil]) => typeof skipUntil === 'number' && skipUntil > Date.now(),
            ),
          )
        : {},
    ...(typeof s.viewerDrawerSizeVh === 'number'
      ? { viewerDrawerSizeVh: s.viewerDrawerSizeVh }
      : {}),
    appEvents: {
      activateTotp: {
        neverShowAgain: s.appEvents.activateTotp.neverShowAgain,
        remindAt: s.appEvents.activateTotp.remindAt,
      },
      ssoLogin: {
        isSsoLogin: s.appEvents.ssoLogin.isSsoLogin,
      },
      tooManyRequests: {
        lockedUntil: s.appEvents.tooManyRequests.lockedUntil,
      },
      fileShare: {
        isNewFilesShareRecipientsReadOnly: s.appEvents.fileShare.isNewFilesShareRecipientsReadOnly,
      },
      tagsList: {
        isExpanded: s.appEvents.tagsList.isExpanded,
      },
    },
  };
}

export type RehydratePersistedSlice = {
  customTokens?: unknown;
  setCustomTokens?: (v?: unknown) => void;
  setToken?: (t?: string) => void;
  directOAuthProviders?: unknown;
  setDirectOAuthProviders?: (v?: unknown) => void;
  authenticatedUserEmail?: unknown;
  setAuthenticatedUserEmail?: (v?: unknown) => void;
  onBehalfOfExpiresAt?: unknown;
  onBehalfOfUser?: unknown;
  setOnBehalfOfData?: (expiresAt: unknown, user: unknown) => void;
};

export function runAppStorePersistRehydration(state: unknown): void {
  if (state == null || typeof state !== 'object') return;
  const st = state as RehydratePersistedSlice;

  const customTokens = st.customTokens;
  if (customTokens) {
    st.setCustomTokens?.(customTokens);
    if (
      customTokens &&
      typeof customTokens === 'object' &&
      customTokens !== null &&
      'token' in customTokens
    ) {
      const t = (customTokens as { token?: string }).token;
      if (t) st.setToken?.(t);
    }
  }

  const directOAuthProviders = st.directOAuthProviders;
  if (directOAuthProviders) st.setDirectOAuthProviders?.(directOAuthProviders);

  const authenticatedUserEmail = st.authenticatedUserEmail;
  if (authenticatedUserEmail) st.setAuthenticatedUserEmail?.(authenticatedUserEmail);

  const onBehalfOfExpiresAt = st.onBehalfOfExpiresAt;
  const onBehalfOfUser = st.onBehalfOfUser;
  if (onBehalfOfExpiresAt !== undefined || onBehalfOfUser !== undefined) {
    st.setOnBehalfOfData?.(onBehalfOfExpiresAt || null, onBehalfOfUser || null);
  }
}
